import sortBy from 'lodash/sortBy';
import React, { useContext, useEffect, useRef } from 'react';

import { useLocation, Link } from 'react-router-dom';

import { CBLocalNavigation } from '@cb/apricot';

import { authorizedRoutes } from '../../../AppRouter';
import { AppStateContext } from '../../../context/AppContext';
import { ResizeContext } from '../../../context/ResizeContext';
import CenterInfo from '../../nonDigital/landing/CenterInfo';
import MenuItem from './MenuItem';
import './LocalNavigation.scss';
import PreviewModeBanner from '../previewMode/PreviewModeBanner';
import { isPreview } from '../../../utils/common';
import { hasAdminAccess } from '../../../utils/user';

/**
 * LocalNav component renders the local navigation bar for the TDTK application.
 *
 * Features:
 * - Displays navigation links based on user authorization.
 * - Adjusts layout and behavior based on window size.
 * - Shows a preview banner if the user is in preview mode.
 *
 * The preview banner is shown only if the user is in preview mode, which is determined by the user's role and active status.
 * This is useful for providing a visual indication to users that they are in a preview environment.
 */

function LocalNav() {
  // Global App state.
  const { orgEvent, user } = useContext(AppStateContext);
  const isAdmin = hasAdminAccess(user.rid, user.role);

  // React Router.
  const { pathname } = useLocation();
  const windowSize = useContext(ResizeContext);

  const localNavRef = useRef<HTMLInputElement>(null);

  const navLinks = user?.id
    ? sortBy(authorizedRoutes(orgEvent, user), ['weight']).filter((link) => link?.localNav)
    : [];

  useEffect(() => {
    CBLocalNavigation({
      elem: document.querySelector('.cb-local-navigation'),
      mobileInteractionClose: true,
      mobileLayoutBreakpoint: 'sm',
      mainContent: document.getElementById('main-content'),
      callBackMobile: (e: React.MouseEvent) => {
        const target = e?.target as HTMLElement;
        const closestLink = target?.closest('a');
        if (closestLink?.className?.includes('cb-disabled')) {
          e?.preventDefault();
        }
      },
      callBackMobileOpen: () => {
        const target = document.getElementById('main-content');
        target?.setAttribute('aria-hidden', 'true');
      },
      callBackMobileClose: () => {
        const target = document.getElementById('main-content');
        target?.removeAttribute('aria-hidden');
      },
    });
  }, [pathname]);

  useEffect(() => {
    if (isPreview(user?.role, user?.mode, user?.active)) {
      document.body.classList.add('tdtk-preview-mode-active');
    } else {
      document.body.classList.remove('tdtk-preview-mode-active');
    }

    return () => {
      document.body.classList.remove('tdtk-preview-mode-active');
    };
  }, [user?.role, user?.active, user?.mode]);

  return (
    <div className='cb-local-navigation cb-tablet-local-navigation cb-white-bg cb-box-shadow-bottom' ref={localNavRef}>
      {/* Show preview banner only if the user is in preview mode. */}
      {isPreview(user?.role, user?.mode, user?.active) && <PreviewModeBanner />}
      <div className='cb-desktop-navigation container'>
        <div className='row'>
          {isAdmin && (
            <div className='d-flex align-items-center'>
              <Link
                id='nav-test-day-live'
                to='/livedata'
                className='cb-btn cb-btn-sm cb-btn-icon-left cb-live-btn cb-btn-black'
              >
                <span className='cb-icon cb-disc-alt cb-green3-color'></span>
                <span>Test Day Live</span>
              </Link>
            </div>
          )}

          <nav
            className={`col-xs cb-nav-container justify-content-${windowSize?.mobile ? 'end' : 'start'}`}
            aria-label='Test Day Toolkit navigation'
          >
            <div className='cb-nav-items hidden-sm-down'>
              <nav className='cb-menu-list cb-menu-list-horizontal' aria-label='Test Day Toolkit site navigation'>
                <ul>
                  {navLinks.map((menuItem) => (
                    <MenuItem
                      key={menuItem?.title}
                      path={menuItem?.path}
                      text={menuItem?.title}
                      disabled={menuItem?.disabled}
                    />
                  ))}
                </ul>
              </nav>
            </div>
            <div className='cb-mobile-trigger hidden-md-up cb-no-border'>
              <a
                aria-hidden={!windowSize?.mobile}
                aria-expanded='false'
                className='cb-icon cb-menu cb-no-text-decoration cb-link-black'
                href='#0'
                id='tdtk-mobile-menu-btn'
              >
                <span className='sr-only'>Display Site Navigation</span>
              </a>
            </div>
          </nav>
          {!windowSize?.mobile ? (
            <div className={`center-info col display--flex_desktop_align-items--center digital`}>
              <CenterInfo />
            </div>
          ) : null}
        </div>
      </div>
      <div className='cb-mobile-navigation tdtk-mobile-navigation hidden-md-up' />
    </div>
  );
}

export default LocalNav;
